import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import Home from '../views/Home';
import DesignSystem from '../pantallas/DesignSystem';
import Login from '../pantallas/Login';
import {
  canAccessOrquestadorRoute,
  getDefaultRouteForSession,
  getOrquestadorSession,
  ORQUESTADOR_ROUTES,
} from '../auth/orquestadorAccess';
import {
  ensureValidOrquestadorSessionForCurrentPageLoad,
  isOrquestadorSessionValidatedForCurrentPageLoad,
} from '../auth/orquestadorAuthApi';
import {
  fetchOrquestadorUpdatedAtLabel,
  getCachedOrquestadorUpdatedAtLabel,
} from '../utils/orquestadorUpdatedAt';

const OrquestadorGlobal = lazy(() => import('../pantallas/OrquestadorGlobal'));
const OrquestadorMahou = lazy(() => import('../pantallas/OrquestadorMahou'));
const OrquestadorOperativo = lazy(() => import('../pantallas/OrquestadorOperativo'));
const OrquestadorProyeccionRecursos = lazy(() => import('../pantallas/OrquestadorProyeccionRecursos'));
const OrquestadorControlGestion = lazy(() => import('../pantallas/OrquestadorControlGestion'));
const OrquestadorDataIa = lazy(() => import('../pantallas/OrquestadorDataIa'));

function OrquestadorSessionValidation() {
  return (
    <main className="login-page" aria-live="polite">
      <section className="login-panel">
        <p className="login-title">
          <span className="login-title__message">Validando sesión...</span>
        </p>
      </section>
    </main>
  );
}

function OrquestadorRouteGuard({ Component, routePath, routeProps }) {
  const [session, setSession] = useState(() => getOrquestadorSession());
  const [isValidatingSession, setIsValidatingSession] = useState(() => (
    !isOrquestadorSessionValidatedForCurrentPageLoad()
  ));

  useEffect(() => {
    let isMounted = true;

    ensureValidOrquestadorSessionForCurrentPageLoad()
      .then(async (validatedSession) => {
        if (!isMounted) {
          return;
        }

        if (validatedSession) {
          const hasCachedUpdatedAt = Boolean(getCachedOrquestadorUpdatedAtLabel());
          const updatedAtPromise = fetchOrquestadorUpdatedAtLabel();

          if (!hasCachedUpdatedAt) {
            await updatedAtPromise;
          }
        }

        if (!isMounted) {
          return;
        }

        setSession(validatedSession);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setSession(null);
      })
      .finally(() => {
        if (isMounted) {
          setIsValidatingSession(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (isValidatingSession) {
    return <OrquestadorSessionValidation />;
  }

  if (!session) {
    return <Redirect to={ORQUESTADOR_ROUTES.LOGIN} />;
  }

  if (!canAccessOrquestadorRoute(session, routePath)) {
    return <Redirect to={getDefaultRouteForSession(session) || ORQUESTADOR_ROUTES.LOGIN} />;
  }

  return (
    <Suspense fallback={<OrquestadorSessionValidation />}>
      <Component {...routeProps} />
    </Suspense>
  );
}

const OrquestadorRoute = ({
  component: Component,
  ...routeProps
}) => (
  <Route
    {...routeProps}
    render={(props) => {
      return (
        <OrquestadorRouteGuard
          Component={Component}
          routePath={routeProps.path}
          routeProps={props}
        />
      );
    }}
  />
);

const AppRouter = () => (
  <Switch>
    <Redirect exact from="/" to={ORQUESTADOR_ROUTES.LOGIN} />
    <Route path="/home" component={Home} />
    <Route path="/DS" component={DesignSystem} />
    <Route path={ORQUESTADOR_ROUTES.LOGIN} component={Login} />
    <Redirect exact from="/proyeccion-recursos" to={ORQUESTADOR_ROUTES.PROYECCION_RECURSOS} />
    <Redirect exact from="/control-gestion" to={ORQUESTADOR_ROUTES.CONTROL_GESTION} />
    <Redirect exact from="/data-ia" to={ORQUESTADOR_ROUTES.DATA_IA} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.GLOBAL} component={OrquestadorGlobal} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.GOBIERNO} component={OrquestadorMahou} />
    <Redirect exact from="/OrquestadorMahou" to={ORQUESTADOR_ROUTES.LOGIN} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.OPERATIVO} component={OrquestadorOperativo} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.PROYECCION_RECURSOS} component={OrquestadorProyeccionRecursos} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.CONTROL_GESTION} component={OrquestadorControlGestion} />
    <OrquestadorRoute path={ORQUESTADOR_ROUTES.DATA_IA} component={OrquestadorDataIa} />
  </Switch>
);

export default AppRouter;
